import React from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { GameTypeInfo, LobbyIcon, PokerVariant } from '@repo/common-types';
import FormField from './form-field';
import FormFields from '@/common/forms/FormFields';
import { maxPlayersOptions } from '@/common/forms/options/MaxPlayerOptions';

interface FormData {
  name: string;
  game: string;
  maxPlayers: number;
  minBuyin?: number | null;
  maxBuyin?: number | null;
  ofcPointValue?: number | null;
  ante?: number | null;
  smallBlind?: number | null;
  bigBlind?: number | null;
  smallBet?: number | null;
  bigBet?: number | null;
  bringIn?: number | null;
  bettingCap?: number | null;
}

export const buildBaseParams = (
  formData: FormData,
  variants: PokerVariant[],
  variantInfo: GameTypeInfo,
  lobbyIcons: LobbyIcon[],
) => ({
  name: formData.name,
  game: formData.game,
  variants,
  maxPlayers: formData.maxPlayers,
  category: variantInfo.category,
  bettingStructure: variantInfo.bettingStructure,
  isPublic: true,
  lobbyIcons,
});

export const buildOFCTemplateParams = (baseParams: ReturnType<typeof buildBaseParams>, formData: FormData) => ({
  ...baseParams,
  // Use lobby icons from baseParams (user-selected), don't override with hardcoded values
  ofcPointValue: formData.ofcPointValue ?? undefined,
  minBuyin: formData.minBuyin ?? 0,
  maxBuyin: formData.maxBuyin ?? 0,
  minStake: formData.ofcPointValue ?? 0,
  maxStake: formData.ofcPointValue ?? 0,
});

export const buildCashGameTemplateParams = (
  baseParams: ReturnType<typeof buildBaseParams>,
  formData: FormData,
  flags: { isStudGame: boolean; isLimitGame: boolean },
) => ({
  ...baseParams,
  ante: formData.ante ?? undefined,
  minBuyin: formData.minBuyin ?? 0,
  maxBuyin: formData.maxBuyin ?? 0,
  smallBlind: formData.smallBlind ?? undefined,
  bigBlind: formData.bigBlind ?? undefined,
  bringIn: flags.isStudGame ? (formData.bringIn ?? undefined) : undefined,
  bettingCap: flags.isLimitGame ? (formData.bettingCap ?? undefined) : undefined,
  smallBet: flags.isLimitGame ? (formData.smallBet ?? undefined) : undefined,
  bigBet: flags.isLimitGame ? (formData.bigBet ?? undefined) : undefined,
  minStake: flags.isLimitGame ? (formData.smallBet ?? 0) : (formData.smallBlind ?? 0),
  maxStake: flags.isLimitGame ? (formData.bigBet ?? 0) : (formData.bigBlind ?? 0),
});

interface FieldsProps {
  control: Control<any>;
  errors: FieldErrors<any>;
}

export const OFCFields = ({ control, errors }: FieldsProps) => (
  <FormFields>
    <FormField
      name="ofcPointValue"
      control={control}
      label="Points Per Hand (USD)"
      error={errors.ofcPointValue?.message as string}
      prefix="$"
    />
    <FormField
      type="select"
      name="maxPlayers"
      control={control}
      label="Table Size (2H or 3H)"
      error={errors.maxPlayers?.message as string}
      options={maxPlayersOptions().filter((opt) => Number(opt.value) <= 3)}
    />
  </FormFields>
);

export const CashGameFields = ({ control, errors }: FieldsProps) => (
  <>
    <FormFields>
      <FormField
        name="smallBlind"
        control={control}
        label="Small Blind"
        error={errors.smallBlind?.message as string}
        prefix="$"
      />
      <FormField
        name="bigBlind"
        control={control}
        label="Big Blind"
        error={errors.bigBlind?.message as string}
        prefix="$"
      />
    </FormFields>
    <FormFields>
      <FormField
        name="smallBet"
        control={control}
        label="Small Bet"
        error={errors.smallBet?.message as string}
        prefix="$"
      />
      <FormField name="bigBet" control={control} label="Big Bet" error={errors.bigBet?.message as string} prefix="$" />
    </FormFields>
    <FormField
      name="bettingCap"
      control={control}
      label="Betting Cap"
      error={errors.bettingCap?.message as string}
      prefix="$"
    />
    <FormField name="bringIn" control={control} label="Bring In" error={errors.bringIn?.message as string} prefix="$" />
    <FormFields>
      <FormField name="ante" control={control} label="Ante" error={errors.ante?.message as string} prefix="$" />
      <FormField
        type="select"
        name="maxPlayers"
        control={control}
        label="Table Size"
        error={errors.maxPlayers?.message as string}
        options={maxPlayersOptions()}
      />
    </FormFields>
  </>
);
